import { useMemo, useRef } from 'react';
import { createSyncFieldSetter } from '../../../domain/persistence/syncSetterFactory';
import { syncExpensePaymentsFromExpensesWithCache } from '../../../domain/persistence/expensePaymentSync';

function dedupeSalesById(sales) {
  const uniqueSales = [];
  const seenIds = new Set();
  for (const sale of sales || []) {
    if (!seenIds.has(sale.id)) {
      seenIds.add(sale.id);
      uniqueSales.push(sale);
    }
  }
  return uniqueSales;
}

/**
 * All *WithSync setters (Strangler extraction from DataContext).
 */
export function useBusinessSyncSetters({
  setters,
  enqueueBusinessFieldSave,
  enqueueBusinessFieldsBatchSave,
  resolveBusinessIdForSave,
  scheduleDataChangeTrigger,
  products,
  currentBusinessId,
  invoiceService,
  Expense,
  getCurrentDateTime,
  businessDataStorageKey
}) {
  const {
    setProducts,
    setPurchases,
    setSales,
    setSuppliers,
    setOrders,
    setCustomers,
    setLots,
    setExpiredProducts,
    setInvoices,
    setInvoicePayments,
    setSupermarketPayments,
    setSupermarketInvoices,
    setSupplierDebts,
    setCustomerDebts,
    setCustomerInvoices,
    setExpenses,
    setRestaurantDishes,
    setStockConsumptions,
    setRestaurantTables,
    setOpenTickets,
    setRecipes,
    setProductionOrders,
    setStockData,
    setWorkerSessions,
    setNotifications,
    setReports,
    setStockLocations,
    setPersistentStockData,
    setOfflineQueue,
    setMetadata,
    setUserInfo,
    setBusinessInfo,
    setDiscrepancyManagement
  } = setters;

  // products/businessId must not be useMemo deps — every Phase-1 products update
  // rebuilt ALL *WithSync setters → every DataContext slice re-rendered app-wide.
  const productsRef = useRef(products);
  productsRef.current = products;
  const currentBusinessIdRef = useRef(currentBusinessId);
  currentBusinessIdRef.current = currentBusinessId;

  const syncDeps = { enqueueBusinessFieldSave, scheduleDataChangeTrigger };

  return useMemo(() => {
    const simple = (setState, field, { trigger = true } = {}) =>
      createSyncFieldSetter({
        setState,
        field,
        enqueueBusinessFieldSave,
        scheduleDataChangeTrigger: trigger ? scheduleDataChangeTrigger : null
      });

    const simpleRestaurantOps = (setState, field) => {
      const base = createSyncFieldSetter({
        setState,
        field,
        enqueueBusinessFieldSave,
        scheduleDataChangeTrigger: null
      });
      return (nextOrUpdater) => {
        let prevSnap;
        let nextSnap;
        base((prev) => {
          prevSnap = prev;
          nextSnap =
            typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
          return nextSnap;
        });
        // Broadcast after setState (not inside updater) — avoids Strict Mode double I/O
        // and same-tick races with the local write.
        if (nextSnap !== prevSnap) {
          const biz = currentBusinessIdRef.current || resolveBusinessIdForSave?.();
          if (biz && Array.isArray(nextSnap)) {
            const payload = nextSnap;
            queueMicrotask(() => {
              void import('../../../infrastructure/storage/restaurantOpsLiveSync')
                .then((m) => {
                  if (field === 'openTickets') m.broadcastOpenTickets(biz, payload);
                  if (field === 'restaurantTables') m.broadcastRestaurantTables(biz, payload);
                })
                .catch(() => {});
            });
          }
        }
      };
    };

    /** Phase 4 — Hybrid enqueue for debt collections after local persist. */
    const simpleWithHybridDebts = (setState, field, kind) => {
      const base = simple(setState, field);
      return (nextOrUpdater) => {
        let prevSnap = [];
        let nextSnap = [];
        base((prev) => {
          prevSnap = Array.isArray(prev) ? prev : [];
          nextSnap =
            typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
          return nextSnap;
        });
        const biz = currentBusinessIdRef.current || resolveBusinessIdForSave?.();
        if (biz && Array.isArray(nextSnap)) {
          void import('../../../services/hybridOpsSync')
            .then(async ({ enqueueHybridOpsSync }) => {
              const { enqueueHybridDeletesForRemoved } = await import(
                '../../../services/hybridDeleteSync'
              );
              await enqueueHybridDeletesForRemoved(biz, kind, prevSnap, nextSnap);
              const { listChangedHybridEntities } = await import(
                '../../../services/hybridDiff'
              );
              const changed = listChangedHybridEntities(kind, prevSnap, nextSnap);
              for (const item of changed) {
                await enqueueHybridOpsSync(biz, kind, item);
              }
            })
            .catch(() => {});
        }
      };
    };

    const setProductsWithSync = createSyncFieldSetter({
      setState: setProducts,
      field: 'products',
      scheduleDataChangeTrigger,
      beforePersist: (next) => {
        if (resolveBusinessIdForSave()) {
          enqueueBusinessFieldSave('products', next);
        }
      }
    });

    const setPurchasesWithSync = (nextOrUpdater) => {
      setPurchases((prev) => {
        const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
        const productsNow = productsRef.current || [];

        if (next.length > prev.length) {
          const newPurchases = next.slice(prev.length);
          const updatedProducts = productsNow.map((product) => {
            const hasNewPurchases = newPurchases.some(
              (purchase) => String(purchase.productId) === String(product.id)
            );
            if (hasNewPurchases) {
              return { ...product, updatedAt: getCurrentDateTime() };
            }
            return product;
          });

          if (updatedProducts.some((p, i) => p.updatedAt !== productsNow[i].updatedAt)) {
            setProducts(updatedProducts);
            if (currentBusinessIdRef.current) {
              enqueueBusinessFieldSave('products', updatedProducts);
            }
          }
        }

        const bid = resolveBusinessIdForSave();
        if (bid) {
          if (process.env.NODE_ENV === 'development') {
            const bucket = businessDataStorageKey(bid);
            console.log(`[ACHAT] Speichern → ${bucket ?? '(kein bucket)'} / purchases`, {
              zeilen: next.length,
              letztePurchaseIds: [...new Set(next.map((p) => p.purchaseId).filter(Boolean))].slice(
                -3
              )
            });
          }
          enqueueBusinessFieldSave('purchases', next);
        }
        scheduleDataChangeTrigger('purchases');
        return next;
      });
    };

    /**
     * UI/state sync for the sales list only.
     * Do NOT use for void/cancel — that must go through domain `voidSale`
     * → `persistSaleVoidAtomically` (restores lots + créance). Hard deletes:
     * repository `removeSale` (no lot restore).
     */
    const setSalesWithSync = (nextOrUpdater) => {
      setSales((prev) => {
        const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
        const finalSales = dedupeSalesById(next);
        const productsNow = productsRef.current || [];

        if (finalSales.length > prev.length) {
          const newSales = finalSales.slice(prev.length);
          const updatedProducts = productsNow.map((product) => {
            const hasNewSales = newSales.some(
              (sale) =>
                sale.products &&
                sale.products.some((sp) => String(sp.productId) === String(product.id))
            );
            if (hasNewSales) {
              return { ...product, updatedAt: getCurrentDateTime() };
            }
            return product;
          });

          if (updatedProducts.some((p, i) => p.updatedAt !== productsNow[i].updatedAt)) {
            setProducts(updatedProducts);
            if (currentBusinessIdRef.current) {
              enqueueBusinessFieldSave('products', updatedProducts);
            }
          }
        }

        enqueueBusinessFieldSave('sales', finalSales);
        scheduleDataChangeTrigger('sales');
        return finalSales;
      });
    };

    const setExpensesWithSync = (nextOrUpdater) => {
      setExpenses((prev) => {
        const nextRaw = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
        const next = (Array.isArray(nextRaw) ? nextRaw : []).map((expense) =>
          expense instanceof Expense ? { ...expense } : expense
        );

        setSupermarketPayments((prevPayments) => {
          const syncedPayments = syncExpensePaymentsFromExpensesWithCache(
            next,
            prevPayments,
            currentBusinessIdRef.current
          );
          enqueueBusinessFieldsBatchSave({
            expenses: next,
            supermarketPayments: syncedPayments
          });
          return syncedPayments;
        });

        scheduleDataChangeTrigger('expenses');
        return next;
      });
    };

    const setSupermarketInvoicesWithSync = createSyncFieldSetter({
      setState: setSupermarketInvoices,
      field: 'supermarketInvoices',
      ...syncDeps,
      afterComputeNext: (raw, prev) => {
        const next = invoiceService.consolidateInvoicesInMemory(
          Array.isArray(raw) ? raw : []
        );
        // Idempotent: no new array / no persist / no dataChange storm
        if (invoiceService.areInvoicesEquivalent(next, prev)) return prev;
        return next;
      },
      shouldPersist: (next, prev) => !invoiceService.areInvoicesEquivalent(next, prev)
    });

    const setWorkerSessionsWithSync = (nextOrUpdater) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[DataContext] setWorkerSessions: UI state only; use sessionRecorder for persistence'
        );
      }
      setWorkerSessions((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    };

    return {
      setProductsWithSync,
      setPurchasesWithSync,
      setSalesWithSync,
      setExpensesWithSync,
      setSupermarketInvoicesWithSync,
      setWorkerSessionsWithSync,
      setSuppliersWithSync: simple(setSuppliers, 'suppliers'),
      setOrdersWithSync: simple(setOrders, 'orders', { trigger: false }),
      setCustomersWithSync: simple(setCustomers, 'customers'),
      setLotsWithSync: simple(setLots, 'lots'),
      setExpiredProductsWithSync: simple(setExpiredProducts, 'expiredProducts', { trigger: false }),
      setInvoicesWithSync: simple(setInvoices, 'invoices'),
      setInvoicePaymentsWithSync: simple(setInvoicePayments, 'invoicePayments'),
      setSupermarketPaymentsWithSync: simple(setSupermarketPayments, 'supermarketPayments'),
      setSupplierDebtsWithSync: simpleWithHybridDebts(
        setSupplierDebts,
        'supplierDebts',
        'supplier_debt'
      ),
      setCustomerDebtsWithSync: simpleWithHybridDebts(
        setCustomerDebts,
        'customerDebts',
        'customer_debt'
      ),
      setCustomerInvoicesWithSync: simple(setCustomerInvoices, 'customerInvoices'),
      setRestaurantDishesWithSync: simple(setRestaurantDishes, 'restaurantDishes', {
        trigger: false
      }),
      setStockConsumptionsWithSync: simple(setStockConsumptions, 'stockConsumptions', {
        trigger: false
      }),
      setRecipesWithSync: simple(setRecipes, 'recipes', {
        trigger: false
      }),
      setProductionOrdersWithSync: simple(setProductionOrders, 'productionOrders', {
        trigger: false
      }),
      setRestaurantTablesWithSync: simpleRestaurantOps(setRestaurantTables, 'restaurantTables'),
      setOpenTicketsWithSync: simpleRestaurantOps(setOpenTickets, 'openTickets'),
      setStockDataWithSync: simple(setStockData, 'stockData', { trigger: false }),
      setNotificationsWithSync: simple(setNotifications, 'notifications', { trigger: false }),
      setReportsWithSync: simple(setReports, 'reports', { trigger: false }),
      setStockLocationsWithSync: simple(setStockLocations, 'stockLocations', { trigger: false }),
      setPersistentStockDataWithSync: simple(setPersistentStockData, 'persistentStockData', {
        trigger: false
      }),
      setOfflineQueueWithSync: simple(setOfflineQueue, 'offlineQueue', { trigger: false }),
      setMetadataWithSync: simple(setMetadata, 'metadata'),
      setUserInfoWithSync: simple(setUserInfo, 'userInfo'),
      setBusinessInfoWithSync: simple(setBusinessInfo, 'businessInfo'),
      setDiscrepancyManagementWithSync: simple(setDiscrepancyManagement, 'discrepancyManagement', {
        trigger: false
      })
    };
  }, [
    setProducts,
    setPurchases,
    setSales,
    setSuppliers,
    setOrders,
    setCustomers,
    setLots,
    setExpiredProducts,
    setInvoices,
    setInvoicePayments,
    setSupermarketPayments,
    setSupermarketInvoices,
    setSupplierDebts,
    setCustomerDebts,
    setCustomerInvoices,
    setExpenses,
    setRestaurantDishes,
    setStockConsumptions,
    setRestaurantTables,
    setOpenTickets,
    setRecipes,
    setProductionOrders,
    setStockData,
    setWorkerSessions,
    setNotifications,
    setReports,
    setStockLocations,
    setPersistentStockData,
    setOfflineQueue,
    setMetadata,
    setUserInfo,
    setBusinessInfo,
    setDiscrepancyManagement,
    enqueueBusinessFieldSave,
    enqueueBusinessFieldsBatchSave,
    resolveBusinessIdForSave,
    scheduleDataChangeTrigger,
    invoiceService,
    Expense,
    getCurrentDateTime,
    businessDataStorageKey
  ]);
}
